/**
 * File validation and UI helpers using content-safety config.
 * All file types and size limits come from config — change in one place (contentSafetyConfig.js or future API).
 */

import { getDefaultConfig } from './contentSafetyConfig.js';

/** @typedef {'image'|'document'} FileContext */

/**
 * Get HTML accept attribute for file inputs.
 * Uses allowedExtensions from config so the picker only shows our formats (e.g. .jpg,.jpeg,.png)
 * and does not show other JPEG variants like .jfif, .pjp, .jpe, .pjpeg.
 * @param {FileContext} context - 'image' | 'document'
 * @param {import('./contentSafetyConfig.js').defaultContentSafetyConfig} [config]
 * @returns {string}
 */
export function getAcceptAttribute(context, config) {
  const cfg = config || getDefaultConfig();
  const fileConfig = context === 'image' ? cfg?.file?.image : cfg?.file?.document;
  const extensions = fileConfig?.allowedExtensions || [];
  if (extensions.length) return extensions.join(',');
  return context === 'image' ? '.jpg,.jpeg,.png,.webp,.heic,.heif' : '.pdf,.doc,.docx,.xls,.xlsx,.txt';
}

/**
 * Get max file size in bytes for the given context.
 * @param {FileContext} context - 'image' | 'document'
 * @param {import('./contentSafetyConfig.js').defaultContentSafetyConfig} [config]
 * @returns {number}
 */
export function getMaxSizeBytes(context, config) {
  const cfg = config || getDefaultConfig();
  const fileConfig = context === 'image' ? cfg?.file?.image : cfg?.file?.document;
  const bytes = fileConfig?.maxSizeBytes;
  if (bytes != null) return bytes;
  return context === 'image' ? 10 * 1024 * 1024 : 20 * 1024 * 1024;
}

/**
 * Get accepted file types in react-dropzone format: { "image/jpeg": [], "image/png": [], ... }.
 * @param {FileContext} context - 'image' | 'document'
 * @param {import('./contentSafetyConfig.js').defaultContentSafetyConfig} [config]
 * @returns {{ [mime: string]: string[] }}
 */
export function getDropzoneAcceptedTypes(context, config) {
  const cfg = config || getDefaultConfig();
  const fileConfig = context === 'image' ? cfg?.file?.image : cfg?.file?.document;
  const types = fileConfig?.allowedMimeTypes || [];
  const obj = {};
  types.forEach((mime) => { obj[mime] = []; });
  return Object.keys(obj).length ? obj : (context === 'image' ? { 'image/jpeg': [], 'image/png': [], 'image/webp': [], 'image/heic': [] } : { 'application/pdf': [] });
}

/**
 * @param {File} file
 * @param {FileContext} context - 'image' | 'document'
 * @param {import('./contentSafetyConfig.js').defaultContentSafetyConfig} [config]
 * @returns {{ valid: boolean, errorCode?: string, message?: string }}
 */
export function validateFile(file, context, config) {
  if (!file || !(file instanceof File)) {
    return { valid: false, errorCode: 'NO_FILE', message: 'No file provided.' };
  }

  const cfg = config || getDefaultConfig();
  const fileConfig = cfg?.file && (context === 'image' ? cfg.file.image : cfg.file.document) ? (context === 'image' ? cfg.file.image : cfg.file.document) : null;
  if (!fileConfig) {
    const allowedTypes = context === 'image' ? ['image/jpeg', 'image/png', 'image/webp', 'image/heic'] : ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'text/plain'];
    const maxSize = context === 'image' ? 10 * 1024 * 1024 : 20 * 1024 * 1024;
    if (!allowedTypes.includes(file.type)) return { valid: false, errorCode: 'FILE_TYPE', message: cfg?.messages?.fileTypeNotAllowed || 'This file type is not allowed.' };
    if (file.size > maxSize) return { valid: false, errorCode: 'FILE_SIZE', message: (cfg?.messages?.fileTooLarge || 'File is too large. Maximum size is {{max}}.').replace('{{max}}', context === 'image' ? '10 MB' : '20 MB') };
    return { valid: true };
  }

  const allowedTypes = fileConfig.allowedMimeTypes || [];
  const blockedExtensions = context === 'document' ? (cfg.file.document.blockedExtensions || []) : (fileConfig.blockedMimeTypes || []);
  const maxSize = fileConfig.maxSizeBytes ?? (context === 'image' ? 10 * 1024 * 1024 : 20 * 1024 * 1024);
  const ext = (file.name || '').toLowerCase().replace(/^.*\./, '.');
  const maxLabel = context === 'image' ? '10 MB' : '20 MB';
  const msgSize = (cfg.messages?.fileTooLarge || 'File is too large. Maximum size is {{max}}.').replace('{{max}}', maxLabel);

  if (context === 'image') {
    if (blockedExtensions.includes(file.type)) {
      return { valid: false, errorCode: 'FILE_TYPE', message: cfg.messages?.fileTypeNotAllowed || 'This file type is not allowed (e.g. no GIF or SVG).' };
    }
    if (!allowedTypes.length || !allowedTypes.includes(file.type)) {
      return { valid: false, errorCode: 'FILE_TYPE', message: cfg.messages?.fileTypeNotAllowed || 'Use JPG, PNG, WEBP or HEIC only.' };
    }
  } else {
    if (blockedExtensions.some((e) => ext === e.toLowerCase())) {
      return { valid: false, errorCode: 'FILE_TYPE', message: cfg.messages?.fileTypeNotAllowed || 'This file type is not allowed.' };
    }
    if (!allowedTypes.length || !allowedTypes.includes(file.type)) {
      return { valid: false, errorCode: 'FILE_TYPE', message: cfg.messages?.fileTypeNotAllowed || 'Use PDF, DOC, DOCX, XLS, XLSX or TXT only.' };
    }
  }

  if (file.size > maxSize) {
    return { valid: false, errorCode: 'FILE_SIZE', message: msgSize };
  }

  return { valid: true };
}
